import { Row } from 'antd';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import { AttributeI, AttributePatch, AttrInternalType } from '~src/interfaces/attributes.types';
import ItemDetailsHeader from '~src/shared/headers/itemDetalsHeader/ItemDetailsHeader';
import TranslatableSectionForm from '~src/shared/form/translatableSectionForm/TranslatableSectionForm';
import {
  changeAttributePrivacy,
  deleteAttribute,
  editAttribute,
} from '~src/modules/attributes/actions/attributes.actions';
import { translateContent } from '~src/services/translationServices';
import logger from '~src/services/logger/LlsLogger';
import { nameDescrFields } from '~src/shared/form/translatableSectionForm/defaultFields';
import { ApiReduxResp, getLoggingContext, isSuccess, ThunkMWDispatch } from '~src/services/apiMiddleware';
import { StoreI } from '~src/rootReducer';
import { TranslatableField } from '~src/shared/form/translatableSectionForm/interfaces';
import { routes } from '~src/routingConfig';
import { SmallTitle, TileContainer } from '~src/styles/containers.sc';

import ToggleRow from './ToggleRow';

const PermissionsContainer = styled(TileContainer)`
  margin-top: 30px;
  padding: 30px;
`;

interface OwnProps {
  attribute: AttributeI;
}

interface StateProps {
  loadingEdit: boolean;
  loadingDel: boolean;
}

interface DispatchProps {
  editAttribute(id: string, attribute: Partial<AttributePatch>): ApiReduxResp<AttributeI>;
  changeAttributePrivacy(id: string, isPrivate: boolean): ApiReduxResp<AttributeI>;
  deleteAttribute(attribute: AttributeI): ApiReduxResp<void>;
}

type Props = OwnProps & StateProps & DispatchProps;

const AttributeHeader: FC<Props> = (props) => {
  const [showEditForm, setShowEditForm] = useState(false);
  const [deletionError, setDeletionError] = useState<null | string>(null);
  const history = useHistory();
  const { t } = useTranslation();

  const getEditAttributeFields = (currAttribute: AttributeI) => {
    return [
      {
        value: currAttribute.name,
      },
      {
        value: currAttribute.description || {},
      },
    ].reduce((acc: TranslatableField[], field, i) => {
      acc[i] = {
        ...nameDescrFields[i],
        ...field,
      };
      return acc;
    }, []);
  };
  const editAttributeSectionFields = [
    {
      name: 'info',
      fields: useMemo(() => getEditAttributeFields(props.attribute), [props.attribute]),
    },
  ];
  const saveSelfAssignableToggle = (isSelfAssignable: boolean): Promise<unknown> => {
    return props.editAttribute(props.attribute.uuid, {self_assignable: isSelfAssignable});
  }

  const savePrivacyChange = (isPrivate: boolean) =>
    props.changeAttributePrivacy(props.attribute.uuid, isPrivate).then((response) => {
      if (isSuccess(response)) {
        return Promise.resolve(response);
      }
      logger.info('Error saving Attribute privacy', getLoggingContext(response, 'AttributeHeader.tsx'));
      return Promise.reject(response);
    });

  const onEditSubmit = (payload: Partial<AttributePatch>) =>
    props.editAttribute(props.attribute.uuid, payload).then((response) => {
      if (isSuccess(response)) {
        toggleEditFrm();
      } else {
        logger.info('Error editing Attribute', getLoggingContext(response, 'AttributeHeader.tsx'));
      }
      return response;
    });

  const deleteThisAttribute = (): ApiReduxResp<void> =>
    props.deleteAttribute(props.attribute).then((resp) => {
      if (isSuccess(resp)) {
        goToList();
      } else {
        logger.info('Error deleting Attribute', getLoggingContext(resp, 'AttributeHeader.tsx'));
        setDeletionError("Error! Can't delete this attribute.");
      }
      return resp;
    });

  const toggleEditFrm = (show = false) => {
    setShowEditForm(show);
  };

  const goToList = () => {
    history.push(routes.company.attributes);
  };

  const { attribute } = props;
  return (
    <ItemDetailsHeader
      t={t}
      isEditFrmVisible={showEditForm}
      deleteItem={deleteThisAttribute}
      loadingDel={props.loadingDel}
      onBackClick={goToList}
      toggleEditFrm={toggleEditFrm}
      backArrow={{
        key: 'x.attributes.details.header.return_link_title',
        txt: t('x.attributes.details.header.return_link_title'),
      }}
      supTitle={{
        key: `x.attributes.details.header.attribute_type, x.attributes.list.types.${attribute.internal_type}`,
        // tslint:disable-next-line:max-line-length
        txt: `${t('x.attributes.details.header.attribute_type')}  ${t(
          `x.attributes.list.types.${attribute.internal_type}`,
        )}`,
      }}
      title={{ key: 'be', txt: translateContent(attribute.name) }}
      description={attribute.description ? { key: 'be', txt: translateContent(attribute.description) } : undefined}
      menuEdit={{
        key: 'x.attributes.details.header.menu.edit_attribute',
        txt: t('x.attributes.details.header.menu.edit_attribute'),
      }}
      menuDel={{
        key: 'x.attributes.details.header.menu.delete_attribute',
        txt: t('x.attributes.details.header.menu.delete_attribute'),
      }}
      deletionTitleKey="x.attributes.delete_confirmation.headline"
      deletionContentKey="x.attributes.delete_confirmation.content"
      deletionError={deletionError}
    >
      <TranslatableSectionForm
        t={t}
        titleKey="be"
        title={translateContent(attribute.name)}
        form={`editAttribute_${attribute.uuid}`}
        sections={editAttributeSectionFields}
        onSubmit={onEditSubmit}
        onCancel={toggleEditFrm}
        loadingSave={props.loadingEdit}
        inContainer
        submitOnlyChanged
      />
      <PermissionsContainer>
        <SmallTitle data-translation="x.attributes.privacy_settings.header">
          {t('x.attributes.privacy_settings.header')}
        </SmallTitle>
        <Row gutter={[30, 16]}>
          {attribute.internal_type === AttrInternalType.Option && (

            <ToggleRow
              initialState={!attribute.hide_from_users}
              onChange={savePrivacyChange}

              translationKeys={{
                label: "x.attributes.privacy_settings.text",
                off: 'x.attributes.privacy_settings.toggle.private',
                on: 'x.attributes.privacy_settings.toggle.not_private'
              }}
            />
          )}
          <ToggleRow
            initialState={attribute.self_assignable}
            onChange={saveSelfAssignableToggle}

            translationKeys={{
              label: 'x.attributes.self_assignable.text',
              off: 'x.attributes.self_assignable.toggle.off',
              on: 'x.attributes.self_assignable.toggle.on'
            }}
          />
        </Row>
      </PermissionsContainer>
    </ItemDetailsHeader>
  );
};

const mapStateToProps = ({ attributes }: StoreI): StateProps => ({
  loadingEdit: attributes.loadingEdit,
  loadingDel: attributes.loadingDel,
});

const mapDispatchToProps = (dispatch: ThunkMWDispatch): DispatchProps => ({
  editAttribute: (id, attribute) => dispatch(editAttribute(id, attribute)),
  changeAttributePrivacy: (id, isPrivate) => dispatch(changeAttributePrivacy(id, isPrivate)),
  deleteAttribute: (attribute) => dispatch(deleteAttribute(attribute)),
});

const AttributeHeaderC = connect(mapStateToProps, mapDispatchToProps)(AttributeHeader);

export default AttributeHeaderC;
